import { Descriptions, Grid } from 'antd';
import {
  getRegimenesDePropiedad,
  getTipoDePredio,
  getUnidadesDeMedidaDeSuperficieDePredios,
  getUsosDePredio,
} from 'api/catastro';
import {
  getClasesDeVehiculos,
  getServiciosDelVehiculo,
  getTipoDeEspacio,
  getTipoDePanteon,
  getTiposDeVehiculos,
} from 'api/recaudacion';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import address from 'utils/address';
import { listToString } from 'utils/normalizers';
import padronToString from 'utils/padronesToString';
import currencyFormatter from 'utils/toCurrency';
import { padronesEnum } from 'views/Tramites/GPM/enums';

const ENV_NAME = process.env.REACT_APP_ENV_NAME || 'migob';
const TLAJOMULCO = ENV_NAME === 'tlajomulco';

const { useBreakpoint } = Grid;

const columnsByTipoDePadron = Object.freeze({
  [padronesEnum.PREDIO]: 4,
  [padronesEnum.LOTES_DE_PANTEON]: 5,
  [padronesEnum.HOSPEDAJE]: 4,
});

// TODO: Quitar cuando se implementen todos los padrones
export const avilablePadrones = Object.freeze([
  padronesEnum.CIUDADANO,
  padronesEnum.EMPRESA,
  padronesEnum.PREDIO,
  padronesEnum.VEHICULO,
  padronesEnum.CONTRIBUYENTE,
  padronesEnum.INFRACCIONES,
  padronesEnum.HOSPEDAJE,
  padronesEnum.LOTES_DE_PANTEON,
  padronesEnum.ARRENDAMIENTO,
  padronesEnum.NOMINA,
  padronesEnum.ALCOHOL,
  padronesEnum.CEDULAR,
  padronesEnum.JUEGO_DE_AZAR,
  padronesEnum.NOTARIO,
  padronesEnum.CASA_DE_EMPENIO,
  padronesEnum.AGENCIA,
  padronesEnum.COMERCIO_INFORMAL,
]);

const formatCiudadano = (padron, { getLada }) => {
  const lada = getLada(padron?.lada);
  const [, nombre = ''] = (padronToString(padron, padronesEnum.CIUDADANO, false) || '').split(' - ');
  return [{
    label: 'Clave Ciudadana',
    value: padron.clave_ciudadana,
    cols: 1,
  },
  {
    label: 'Nombre',
    value: nombre,
    cols: 2,
  },
  {
    label: 'RFC',
    value: padron.RFC,
    cols: 1,
  },
  {
    label: 'CURP',
    value: padron.CURP,
    cols: 1,
  },
  {
    label: 'Correo Electrónico',
    value: padron.email,
    cols: 1,
  },
  {
    label: 'Telefono',
    value: `+${lada} ${padron.numero_de_celular}`,
    cols: 1,
  },
  ];
};

const formatContribuyente = (padron, { getLada }) => {
  const lada = getLada(padron.lada);
  const [clave = '', name = ''] = (padronToString(padron, padronesEnum.CONTRIBUYENTE, false) || '').split(' - ');

  return [
    {
      label: 'Clave',
      value: clave,
      cols: 1,
    },
    {
      label: 'Nombre',
      value: name,
      cols: 2,
    },
    {
      label: 'RFC',
      value: padron?.rfc,
      cols: 1,
    },
    {
      label: 'Correo Electrónico',
      value: padron?.correo_electronico,
      cols: 1,
    },
    {
      label: 'Telefono',
      value: padron?.telefono_celular
        ? `+${lada} ${padron.telefono_celular}` : '',
      cols: 1,
    },
  ].map((item) => ({ ...item, value: item?.value || '' }));
};

const formatEmpresa = (padron, { getLada }) => {
  const lada = getLada(padron.lada_celular);
  const padronString = padronToString(padron, padronesEnum.EMPRESA, false) || '';
  return [
    {
      label: TLAJOMULCO ? 'Objeto Cuenta - Denominación/Nombre comercial' : 'Nombre',
      value: padronString,
      cols: 2,
    },
    {
      label: 'RFC',
      value: padron?.contribuyente?.rfc,
      cols: 1,
    },
    {
      label: 'Correo Electrónico',
      value: padron?.correo_electronico,
      cols: 1,
    },
    {
      label: 'Telefono',
      value: `+${lada} ${padron?.telefono_celular || ''}`,
      cols: 1,
    },
  ];
};

const formatPredio = (padron, {
  getPropietarioDePredio,
  getMedidasDePredio,
  usosDePredio,
  regimenesDePropiedad,
}) => [
  {
    label: 'Clave catastral estándar',
    value: padron.clave_catastral_estandar,
    cols: 1,
  },
  {
    label: 'Cuenta unica predial',
    value: padron?.cuenta_unica_de_predial || padron?.clave_catastral_estandar,
    cols: 1,
  },
  {
    label: 'No. Cuenta',
    value: padron.clave_catastral_municipal,
    cols: 1,
  },
  {
    label: 'CURT',
    value: padron.CURT,
    cols: 1,
  },
  {
    label: 'Propietario',
    value: getPropietarioDePredio(padron),
    cols: 2,
  },
  {
    label: 'Direccion',
    value: address(padron.direccion_index) || padron?.direccion,
    cols: 2,
  },
  {
    label: 'Valor Fiscal',
    value: currencyFormatter(padron?.valor_fiscal || 0),
    cols: 1,
  },
  {
    label: 'Tipo de Predio',
    value: padron.tipo_de_predio?.descripcion,
    cols: 1,
  },
  {
    label: 'Uso de Predio',
    value: usosDePredio.find((u) => u.id === padron.uso_de_predio)?.descripcion,
    cols: 1,
  },
  {
    label: 'Regimen de Propiedad',
    value: regimenesDePropiedad.find((r) => r.id === padron.regimen_de_propiedad)
      ?.descripcion,
    cols: 1,
  },
  {
    label: 'Superficie de Terreno',
    value: getMedidasDePredio(padron),
    cols: 1,
  },
  {
    label: 'Valor de Terreno',
    value: currencyFormatter(padron?.valor_del_terreno || 0),
    cols: 1,
  },
  {
    label: 'Superficie de Construccion',
    value: getMedidasDePredio(padron, 'superficie_de_la_construccion'),
    cols: 1,
  },
  {
    label: 'Valor de Construccion',
    value: currencyFormatter(padron?.valor_de_la_construccion || 0),
    cols: 1,
  },
].map((item) => ({ ...item, value: item?.value || '' }));

const formatVehiculo = (padron, {
  serviciosDelVehiculo,
  tiposDeVehiculos,
  clasesDeVehiculos,
}) => {
  const {
    clase_del_vehiculo,
    servicio,
    tipo_de_vehiculo,
  } = padron;

  const padronAsString = padronToString(padron, padronesEnum.VEHICULO, false) || '';
  return [
    {
      label: 'Placas/Serie',
      value: padronAsString,
      cols: 2,
    },
    {
      label: 'Clase',
      value: clasesDeVehiculos.find((c) => c.id === clase_del_vehiculo)?.nombre
      || '',
      cols: 1,
    },
    {
      label: 'Servicio',
      value: serviciosDelVehiculo.find((s) => s.id === servicio)?.nombre,
      cols: 1,
    },
    {
      label: 'Tipo de Vehiculo',
      value: tiposDeVehiculos.find((t) => t.id === tipo_de_vehiculo)?.nombre,
      cols: 1,
    },
  ].map((item) => ({ ...item, value: item?.value || '' }));
};

const formatPanteon = (padron) => {
  let propietario = '';
  let direccion = '';
  if (padron?.contributente) {
    const [, name = ''] = (padronToString(padron.contributente, padronesEnum.CONTRIBUYENTE, false) || '')
      .split(' - ');
    propietario = name;
    const { direcciones: dir } = padron.contributente;
    direccion = address(dir);
  } else { propietario = padron.nombre_completo_ciudadano; }

  return [
    {
      label: 'Clave / Folio',
      value: padron.folio,
      cols: 1,
    },
    {
      label: 'Nombre del Propietario',
      value: propietario,
      cols: 2,
    },
    {
      label: 'Dirección',
      value: direccion,
      cols: 2,
    },
    {
      label: 'Panteón',
      value: padron?.nombre_completo_ciudadano,
      cols: 2,
    },
    {
      label: 'Cantidad de espacios',
      value: padron?.cantidad,
      cols: 1,
    },
    {
      label: 'Tipo de espacio',
      value: padron?.espacio?.nombre || padron?.espacio?.descripcion || '',
      cols: 2,
    },
  ];
};

const formatInfracciones = (padron) => {
  const [clave = '', name = ''] = (padronToString(padron, padronesEnum.INFRACCIONES, false) || '').split(' - ');
  return [
    {
      label: 'Folio',
      value: padron.folio,
      cols: 1,
    },
    {
      label: 'Clave',
      value: clave,
      cols: 1,
    },
    {
      label: 'Nombre',
      value: name,
      cols: 1,
    },
  ];
};

const InfoPadron = ({ padron, tipoDePadron }) => {
  const screens = useBreakpoint();
  const {
    ladas,
    usosDePredio,
    // unidadesDeMedidaPredios: unidades,
    regimenesDePropiedad,
    serviciosDelVehiculo,
    tiposDeVehiculos,
    clasesDeVehiculos,
  } = useSelector((state) => state.catalogs);
  const [padronProps, setPadronProps] = useState([]);

  const getLada = useCallback((ladaID) => (
    ladas.find((l) => l.id === ladaID)?.lada || 52
  ), [ladas]);

  const getPropietarioDePredio = useCallback((_padron) => (
    _padron?.participe_principal
    ?? padronToString(_padron?.ciudadano, padronesEnum.CIUDADANO, false)
      .split(' - ')[1]
    ?? listToString(_padron?.nombres_de_participes)
  ), []);

  const getMedidasDePredio = useCallback((_padron, key = 'superficie_total_del_terreno') => {
    const number = _padron[key] || 0;
    if (!number) return '';
    return `${number}`;
  }, []);

  const defaultFormatter = useCallback((_padron) => {
    const [clave = '', name = ''] = (padronToString(_padron, tipoDePadron, false) || '').split(' - ');
    const { empresa } = _padron || {};
    const { contribuyente } = empresa || {};
    const [, altName = ''] = (padronToString(contribuyente, padronesEnum.CONTRIBUYENTE, false) || '')
      .split(' - ');
    const ladaFijo = getLada(_padron.lada);
    const ladaCelular = getLada(_padron.lada_celular);

    return [
      {
        label: 'Clave',
        value: clave,
        cols: 1,
      },
      {
        label: 'Nombre',
        value: name || altName,
        cols: 2,
      },
      {
        label: 'RFC',
        value: contribuyente?.rfc || '',
        cols: 1,
      },
      {
        label: 'Correo Electrónico',
        value: empresa?.correo_electronico || contribuyente?.correo_electronico || '',
        cols: 1,
      },
      {
        label: 'Telefono Fijo',
        value: empresa?.telefono_fijo
          ? `+${ladaFijo} ${empresa.telefono_fijo}` : '',
        cols: 1,
      },
      {
        label: 'Telefono Celular',
        value: empresa?.telefono_celular
          ? `+${ladaCelular} ${empresa.telefono_celular}` : '',
      },
    ];
  }, [getLada, tipoDePadron]);

  const padronesFormatter = useMemo(() => ({
    [padronesEnum.CIUDADANO]: (_padron) => formatCiudadano(_padron, { getLada }),
    [padronesEnum.EMPRESA]: (_padron) => formatEmpresa(_padron, { getLada }),
    [padronesEnum.PREDIO]: (_padron) => formatPredio(_padron, {
      getPropietarioDePredio,
      getMedidasDePredio,
      usosDePredio,
      regimenesDePropiedad,
    }),
    [padronesEnum.VEHICULO]: (_padron) => formatVehiculo(_padron, {
      serviciosDelVehiculo,
      tiposDeVehiculos,
      clasesDeVehiculos,
    }),
    [padronesEnum.CONTRIBUYENTE]: (_padron) => formatContribuyente(_padron, { getLada }),
    [padronesEnum.INFRACCIONES]: formatInfracciones,
    [padronesEnum.LOTES_DE_PANTEON]: formatPanteon,
  }), [
    clasesDeVehiculos,
    getLada,
    getMedidasDePredio,
    getPropietarioDePredio,
    regimenesDePropiedad,
    serviciosDelVehiculo,
    tiposDeVehiculos,
    usosDePredio,
  ]);

  const fetchCatalogs = useCallback(async () => {
    try {
      if (padronesEnum.PREDIO === tipoDePadron) {
        const needsTipo = !regimenesDePropiedad?.length;
        if (needsTipo) {
          await Promise.all([
            getTipoDePredio(),
            getUsosDePredio(),
            getUnidadesDeMedidaDeSuperficieDePredios(),
            getRegimenesDePropiedad(),
          ]);
        }
      }
      if (padronesEnum.VEHICULO === tipoDePadron) {
        const needsVehiculo = !serviciosDelVehiculo?.length
          || !tiposDeVehiculos?.length
          || !clasesDeVehiculos?.length;
        if (needsVehiculo) {
          await Promise.all([
            getServiciosDelVehiculo(),
            getTiposDeVehiculos(),
            getClasesDeVehiculos(),
          ]);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error cargando catálogos de padrones', error);
      throw error;
    }
  }, [
    clasesDeVehiculos,
    regimenesDePropiedad,
    serviciosDelVehiculo,
    tipoDePadron,
    tiposDeVehiculos,
  ]);

  const dynamicCacheRef = React.useRef({});

  const resolveDynamicProps = useCallback(async (_padron) => {
    if (tipoDePadron !== padronesEnum.LOTES_DE_PANTEON) {
      return _padron;
    }

    const config = [
      {
        prop: 'panteon',
        fetcher: getTipoDePanteon,
      },
      {
        prop: 'espacio',
        fetcher: getTipoDeEspacio,
      },
    ];

    const toResolve = config.filter(({ prop }) => _padron?.[prop]);
    if (!toResolve.length) return _padron;

    const cache = dynamicCacheRef.current;

    const results = await Promise.all(
      toResolve.map(({ prop, fetcher }) => {
        const cacheKey = `${prop}-${_padron[prop]}`;
        if (cacheKey in cache) {
          return cache[cacheKey];
        }
        return fetcher(_padron[prop]).then((response) => {
          cache[cacheKey] = response;
          return response;
        });
      }),
    );

    return toResolve.reduce((acc, { prop }, index) => ({
      ...acc,
      [prop]: results[index],
    }), _padron);
  }, [tipoDePadron]);

  useEffect(() => {
    let mounted = true;

    const loadInfo = async () => {
      if (!padron || !tipoDePadron) {
        if (mounted) setPadronProps([]);
        return;
      }

      try {
        await fetchCatalogs();
        const enrichedPadron = await resolveDynamicProps(padron);
        if (!mounted) return;

        const formatter = padronesFormatter[tipoDePadron] || defaultFormatter;
        if (!formatter) {
          setPadronProps([]);
          return;
        }

        setPadronProps(formatter(enrichedPadron, tipoDePadron));
      } catch (error) {
        if (mounted) setPadronProps([]);
      }
    };

    loadInfo();

    return () => {
      mounted = false;
    };
  }, [
    defaultFormatter,
    fetchCatalogs,
    padron,
    padronesFormatter,
    resolveDynamicProps,
    tipoDePadron,
  ]);

  if (!padronProps.length || !padron) {
    return null;
  }

  return (
    <Descriptions
      bordered
      size="small"
      layout="vertical"
      column={screens.md ? (columnsByTipoDePadron[tipoDePadron] || 3) : 1}
    >
      {padronProps.map((prop) => {
        const baseValue = typeof prop?.value === 'object'
          ? prop?.value?.id || prop?.value?.descripcion || prop?.value?.nombre || ''
          : prop?.value;
        const itemKey = `${prop.label}-${baseValue}`;
        return (
          <Descriptions.Item
            key={itemKey}
            label={<strong>{prop?.label}</strong>}
            span={screens.md ? prop?.cols : 1}
          >
            {prop?.value}
          </Descriptions.Item>
        );
      })}
    </Descriptions>
  );
};

InfoPadron.propTypes = {
  padron: PropTypes.shape({
    id: PropTypes.number,
    panteon: PropTypes.number,
    espacio: PropTypes.number,
    clave_ciudadana: PropTypes.string,
    RFC: PropTypes.string,
    CURP: PropTypes.string,
    email: PropTypes.string,
    numero_de_celular: PropTypes.string,
    lada: PropTypes.number,
    lada_celular: PropTypes.number,
    telefono_celular: PropTypes.string,
    contribuyente: PropTypes.shape({
      rfc: PropTypes.string,
    }),
    correo_electronico: PropTypes.string,
    direccion: PropTypes.string,
    clave_catastral_estandar: PropTypes.string,
    cuenta_unica_de_predial: PropTypes.string,
    clave_catastral_municipal: PropTypes.string,
    CURT: PropTypes.string,
    participes: PropTypes.arrayOf(PropTypes.shape({
      ciudadano: PropTypes.shape({
        nombres: PropTypes.string,
        apellido_paterno: PropTypes.string,
        apellido_materno: PropTypes.string,
      }),
    })),
    ciudadano: PropTypes.shape({
      nombres: PropTypes.string,
      apellido_paterno: PropTypes.string,
      apellido_materno: PropTypes.string,
    }),
    tipo_de_predio: PropTypes.shape({
      id: PropTypes.number,
      descripcion: PropTypes.string,
    }),
    uso_de_predio: PropTypes.number,
    regimen_de_propiedad: PropTypes.number,
    superficie_total_del_terreno: PropTypes.number,
    unidad_de_medida_de_la_superficie: PropTypes.number,
    valor_del_terreno: PropTypes.number,
    superficie_de_la_construccion: PropTypes.number,
    valor_de_la_construccion: PropTypes.number,
    clase_del_vehiculo: PropTypes.number,
    servicio: PropTypes.number,
    tipo_de_vehiculo: PropTypes.number,
    direccion_index: PropTypes.shape({
    }),
  }),
  tipoDePadron: PropTypes.oneOf(Object.values(padronesEnum)).isRequired,
};

InfoPadron.defaultProps = {
  padron: null,
};

export default InfoPadron;
